import {useState} from 'react';
import {format} from 'date-fns';

import {Card, Icon, Text} from '#component/base';
import {ProgressBar} from '#component/base/ProgressBar';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {AccordionRoot, InnerAccordion} from '#component/util';
import {sprinkles} from '#style/sprinkles';

import {TestResultListItemProps} from './TestResultListItem.types';

import * as css from './TestResultListItem.css';

export const TestResultListItem = ({
  categories = [],
  count = 0,
  deltaAge = 0,
  formattedBioAge,
  formattedDeltaAge,
  isCurrent,
  lifestyleScore,
  registeredDate,
}: TestResultListItemProps) => {
  const [open, setOpen] = useState(false);
  const {track} = useAnalytics();

  const handleAccordionClick = () => {
    const nextOpen = !open;

    setOpen(nextOpen);

    if (nextOpen && count) {
      track<UserActionEvent>({
        actionName: 'Opened result accordion',
        data: {testNumber: count},
        evtName: EventType.UserAction,
      });
    }
  };

  const trigger = (
    <>
      <span className={sprinkles({content: 'between', flex: 'flex', items: 'center', w: 'full'})}>
        <Text color="doveGray" variant="caption">
          {registeredDate && format(new Date(registeredDate), 'LLL d, yyyy')}
        </Text>

        {!!categories.length && (
          <Icon
            className={css.icon}
            color="doveGray"
            size="small"
            name="CaretDown"
            style={{rotate: open ? '-180deg' : '0deg'}}
          />
        )}
      </span>

      <ul
        className={sprinkles({gap: 24, w: 'full'})}
        style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)'}}>
        <li className={sprinkles({flex: 'flex', dir: 'col', gap: 4})}>
          <Text color="mineShaft" fontWeight="semiBold" textAlign="left">
            {formattedBioAge ? `${formattedBioAge.years}y ${formattedBioAge.months}m` : '—'}
          </Text>

          <Text color="mineShaft" textAlign="left" variant="caption">
            TallyAge™
          </Text>
        </li>

        <li className={sprinkles({flex: 'flex', dir: 'col', gap: 4})}>
          <Text color="mineShaft" fontWeight="semiBold" textAlign="left">
            {formattedDeltaAge
              ? `${deltaAge < 0 ? '-' : '+'}${formattedDeltaAge.years}y ${
                  formattedDeltaAge.months
                }m`
              : '—'}
          </Text>

          <Text color="mineShaft" textAlign="left" variant="caption">
            Delta
          </Text>
        </li>

        <li className={sprinkles({flex: 'flex', dir: 'col', gap: 4})}>
          <Text color="mineShaft" fontWeight="semiBold" textAlign="left">
            {lifestyleScore ? lifestyleScore : '—'}
          </Text>

          <Text color="mineShaft" textAlign="left" variant="caption">
            Lifestyle Score
          </Text>
        </li>
      </ul>
    </>
  );

  return (
    <>
      {!!categories.length && (
        <AccordionRoot collapsible onValueChange={handleAccordionClick} type="single">
          <Card
            as={InnerAccordion}
            trigger={trigger}
            triggerClassName={sprinkles({
              flex: 'flex',
              w: 'full',
              dir: 'col',
              gap: 12,
            })}
            variant={open ? 'white' : 'cotton'}
            value="categories">
            <Text as="ul" className={sprinkles({flex: 'flex', dir: 'col', gap: 24, pT: 24})}>
              {categories.map(({title, categoryScore, maxCategoryScore, id}) => (
                <li className={sprinkles({flex: 'flex', dir: 'col', gap: 8})} key={id}>
                  <span className={sprinkles({flex: 'flex', dir: 'col'})}>
                    <Text
                      className={sprinkles({flex: 'flex', content: 'between'})}
                      color="mineShaft">
                      {title}

                      <span>{categoryScore}</span>
                    </Text>
                  </span>

                  <ProgressBar
                    aria-label="Category score"
                    value={categoryScore}
                    maxValue={maxCategoryScore}
                  />
                </li>
              ))}
            </Text>
          </Card>
        </AccordionRoot>
      )}

      {!categories.length && (
        <Card
          variant="cotton"
          className={sprinkles({
            flex: 'flex',
            w: 'full',
            dir: 'col',
            gap: 12,
          })}>
          {trigger}
        </Card>
      )}
    </>
  );
};
